import React, { useState, FC, useEffect } from 'react';
import Box from '@mui/material/Box';
import './TwDispatchNotes.scss';

import TwDispatchNotesTextEditor from './TwDispatchNotesTextEditor';
import TwDispatchNotesMessages from './TwDispatchNotesMessages';
import TwDispatchNotesLoading from './TwDispatchNotesLoading';
import TwDispatchNotesEmpty from './TwDispatchNotesEmpty';

import useNotifications from '../../providers/Notifications/Notifications.provider';
import { USER_ROLE } from '../../providers/User/User.model';
import TwDialog from '../TwDialog/TwDialog';
import { TwButton, TwButtonType } from '../TwButtons/TwButtons';
import { ReactComponent as WarningSign } from '../../assets/warning-sign.svg';
import Typography from '@mui/material/Typography';

import useScrollToView from '../../hooks/useScrollToView';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material';
import {
  useDeleteDispatchNoteMutation,
  useGetDispatchNotesQuery
} from '../../redux/api/orders/ordersApi';

export enum DispatchNoteType {
  DISPATCH_ONLY = 'DISPATCH_ONLY',
  DISPATCH_CARRIER_AND_DRIVERS = 'DISPATCH_CARRIER_AND_DRIVERS'
}

export interface DispatchNote {
  created_at: Date;
  created_by: string;
  id: string;
  note: string;
  note_type: DispatchNoteType;
  order_id: string | null;
  trip_id: string | null;
  updated_at: Date;
  updated_by: string;
}

export interface DispatchNoteResponse {
  dispatch_note: DispatchNote;
  user: {
    first_name: string;
    id: string;
    initials: string;
    last_name: string;
    role: USER_ROLE;
  };
  tripSortOrder?: number;
  tripReadableId?: number | string;
}

export interface CreateDispatchNote {
  note: string;
  note_type: DispatchNoteType;
  tripId?: string;
  orderId?: string;
  token?: string;
}

export interface TwDispatchNotesProps {
  orderId?: string;
  tripId?: string;
  isSplitLoad: boolean;
  isDriverLink?: boolean;
  driverId?: string;
  token?: string;
  loadReadableId: number;
  reloadData?: () => void;
  componentRef?: React.RefObject<HTMLDivElement>;
  ltlEnabled: boolean;
}

const TwDispatchNotes: FC<TwDispatchNotesProps> = ({
  orderId,
  tripId,
  isDriverLink,
  token,
  driverId,
  loadReadableId,
  componentRef,
  reloadData,
  ltlEnabled
}): JSX.Element => {
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState<boolean>(false);
  const [noteToDelete, setNoteToDelete] = useState<DispatchNoteResponse | null>(null);
  const [dispatchNotes, setDispatchNotes] = useState<DispatchNoteResponse[]>([]);
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const latestNoteRef = React.useRef<HTMLDivElement | null>(null);

  const [scrollIntoView] = useScrollToView();
  const [deleteDispatchNote] = useDeleteDispatchNoteMutation();
  const { addNotification } = useNotifications();
  const {
    data: notes,
    isLoading,
    refetch: getNotes
  } = useGetDispatchNotesQuery({
    orderId,
    tripId,
    token,
    order: matchesMobileOrTablet ? 'asc' : 'desc'
  });

  useEffect(() => {
    if (notes) setDispatchNotes(notes);
  }, [notes]);

  useEffect(() => {
    // Scroll to the latest note
    matchesMobileOrTablet && scrollIntoView(latestNoteRef, 'center');
  }, [scrollIntoView, matchesMobileOrTablet]);

  const handleReloadData = (): void => {
    getNotes();
    reloadData && reloadData();
  };

  const handleDelete = async (): Promise<void> => {
    if (noteToDelete) {
      try {
        deleteDispatchNote({ noteId: noteToDelete.dispatch_note.id, token: token })
          .unwrap()
          .then((res) => {
            handleReloadData();
            closeDeleteModal();
            addNotification({
              message: 'Note deleted successfully',
              variant: 'success'
            });
          })
          .catch((e) => {
            addNotification({
              message: e.message || 'Error deleting note, please try again',
              variant: 'error'
            });
          });
      } catch (error: any) {
        addNotification({
          message: error.message || 'Error trying to delete note, please try again',
          variant: 'error'
        });
      }
    }
  };

  const closeDeleteModal = (): void => {
    setOpenDeleteConfirmation(false);
    setNoteToDelete(null);
  };

  const setNoteToDeleteAndOpenModal = (note: DispatchNoteResponse): void => {
    setNoteToDelete(note);
    setOpenDeleteConfirmation(true);
  };

  const formatUserName = (firstName: string, lastName: string): string => {
    const lastLetter = lastName.slice(-1);

    return `${firstName} ${lastName}${lastLetter === 's' ? "'" : "'s"}`;
  };

  return (
    <>
      <Box className="dispatch-notes" ref={componentRef}>
        <TwDispatchNotesTextEditor
          orderId={orderId}
          tripId={tripId}
          isDriverLink={isDriverLink}
          token={token}
          refresh={handleReloadData}
        />

        {isLoading ? (
          <TwDispatchNotesLoading />
        ) : notes?.length === 0 ? (
          <TwDispatchNotesEmpty />
        ) : (
          <Box className="dispatch-notes__notes-container">
            {dispatchNotes.map((note: DispatchNoteResponse, index: number) => {
              return (
                <div key={note.dispatch_note.id}>
                  <TwDispatchNotesMessages
                    note={note}
                    orderReadableId={loadReadableId}
                    isDriverLink={isDriverLink}
                    driverId={driverId}
                    handleDeleteNote={setNoteToDeleteAndOpenModal}
                    ltlEnabled={ltlEnabled}
                  />
                  {matchesMobileOrTablet && index === dispatchNotes.length - 1 && (
                    <div ref={latestNoteRef} />
                  )}
                </div>
              );
            })}
          </Box>
        )}
      </Box>
      <TwDialog
        size="xs"
        title="Delete note?"
        open={openDeleteConfirmation}
        onClickClose={closeDeleteModal}
        actions={[
          <TwButton
            key="1"
            type={TwButtonType.secondary}
            label="Cancel"
            action={closeDeleteModal}
          />,
          <TwButton key="2" type={TwButtonType.negative} label="Delete" action={handleDelete} />
        ]}
      >
        <Box>
          <Box sx={{ textAlign: 'center' }}>
            <WarningSign />
          </Box>
          <Typography fontSize={14} color={'#6B7280'}>
            Are you sure you want to delete{' '}
            <Typography fontSize={14} color={'#6B7280'} component={'span'} fontWeight={'500'}>
              {formatUserName(
                noteToDelete?.user.first_name || '',
                noteToDelete?.user.last_name || ''
              )}
            </Typography>{' '}
            note?
          </Typography>
          <Typography fontSize={14} color={'#6B7280'}>
            {`You can't undo this action.`}
          </Typography>
        </Box>
      </TwDialog>
    </>
  );
};

export default TwDispatchNotes;
